'use client';

import { Accordion, Icon } from '@/components/ui';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { cn } from '@/libs';
import { RaffleExtractionType } from '@/types/enums/HighlightedRaffle';
import { BrandingKey } from '@/types/enums/WhitelabelConfig';
import type { HighlightedRaffleDetails } from '@/types/models/HighlightedRaffleDetails';

import { sortPrizesByValue } from './utils';

import { ExtractionMethodWrapper } from '../ExtractionMethodWrapper';
import { PrizeCard } from '../PrizeCard';
import { PrizesNavigation } from '../PrizesNavigation';

import { usePrizes } from '../../hooks';

type MainPrize = NonNullable<HighlightedRaffleDetails['mainPrizes']>[0];

type PrizesAccordionProps = {
  prizes: MainPrize[];
  extractionType?: string;
  drawnSequence?: string[];
  drawDate?: string;
  isFinished?: boolean;
  className?: string;
};

export const PrizesAccordion = ({
  prizes,
  extractionType,
  drawnSequence,
  drawDate,
  isFinished,
  className,
}: PrizesAccordionProps) => {
  const sortedPrizes = sortPrizesByValue(prizes);
  const { getValue } = useWhitelabelConfig();
  const prizesFooterText = getValue(BrandingKey.BRANDING_PRIZES_FOOTER_TEXT);

  const {
    currentPrize,
    currentIndex,
    totalPrizes,
    showNavigation,
    canGoNext,
    canGoPrev,
    goToNext,
    goToPrev,
    showExtractionMethod,
    isMobile,
  } = usePrizes({ prizes: sortedPrizes, extractionType });

  if (!prizes?.length) {
    return null;
  }

  const renderDesktopContent = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedPrizes.map((prize, index) => (
          <div key={prize.id || index}>
            <PrizeCard
              prize={prize}
              index={index}
              totalPrizes={sortedPrizes.length}
              className="p-4 rounded-lg"
            />
            {extractionType === RaffleExtractionType.LOTTERY && (
              <ExtractionMethodWrapper
                prize={prize}
                drawnSequence={drawnSequence}
                drawDate={drawDate}
                isFinished={isFinished}
                className="p-4"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderMobileContent = () => (
    <div className="space-y-4 pt-2">
      {totalPrizes > 0 && (
        <PrizesNavigation
          currentIndex={currentIndex}
          totalPrizes={totalPrizes}
          canGoNext={canGoNext}
          canGoPrev={canGoPrev}
          onNext={goToNext}
          onPrev={goToPrev}
          showButtons={showNavigation}
        />
      )}

      {currentPrize && (
        <div>
          <PrizeCard
            prize={currentPrize}
            index={currentIndex}
            totalPrizes={sortedPrizes.length}
            className="bg-background-variant rounded-lg"
          />
          {showExtractionMethod && (
            <ExtractionMethodWrapper
              prize={currentPrize}
              drawnSequence={drawnSequence}
              drawDate={drawDate}
              isFinished={isFinished}
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <Accordion
      showLine={false}
      className={cn('w-full p-4 bg-background-dark rounded-xl', className)}
      headerContent={
        <div className="flex items-center space-x-2">
          <Icon.Trophy width={16} height={16} className="fill-brand" />
          <h3 className="text-left font-bold">Premiações</h3>
        </div>
      }
    >
      {isMobile ? renderMobileContent() : renderDesktopContent()}
      {prizesFooterText && (
        <span className="pt-4 block text-foreground text-justify">
          {prizesFooterText}
        </span>
      )}
    </Accordion>
  );
};
